import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Radar, ShieldCheck, Network, GitCompareArrows, ArrowRight, Database, Mail, Loader2, ArrowLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("choose"); // choose | email
  const [step, setStep] = useState("email");   // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!loading && user) navigate("/app/dashboard", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (resendIn <= 0) return;
    timerRef.current = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [resendIn]);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const requestCode = async (e) => {
    e?.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      const r = await api.post("/auth/email/request-code", { email: email.trim() });
      setNotice(r.data.message);
      setStep("code");
      setResendIn(r.data.resend_in || 60);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send the code. Please try again.");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setError(""); setBusy(true);
    try {
      const r = await api.post("/auth/email/verify-code", { email: email.trim(), code: code.trim() });
      setUser(r.data);
      navigate("/app/dashboard", { state: { user: r.data } });
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid code. Please try again.");
    } finally { setBusy(false); }
  };

  const resetEmail = () => {
    setMode("choose"); setStep("email"); setCode(""); setError(""); setNotice(""); setResendIn(0);
  };

  const inputCls = "w-full rounded-lg border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none transition-colors duration-150";

  return (
    <div className="radar-grain min-h-screen bg-slate-950 text-slate-100 flex flex-col lg:flex-row">
      {/* Left: brand / pitch */}
      <div className="relative flex-1 flex flex-col justify-between p-8 sm:p-14 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-blue-600/10 blur-3xl" />
        <div className="absolute bottom-10 right-0 w-80 h-80 rounded-full bg-emerald-500/5 blur-3xl" />

        <div className="relative z-10 flex items-center gap-3">
          <Radar className="w-8 h-8 text-blue-400" />
          <div className="leading-none">
            <div className="text-2xl font-extrabold tracking-tight">RADAR</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">NGO Crosswalk Intelligence</div>
          </div>
        </div>

        <div className="relative z-10 max-w-xl">
          <p className="font-mono text-xs uppercase tracking-widest text-blue-400 mb-4">Due-diligence · Verified</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
            One identity across every Indian NGO registry.
          </h1>
          <p className="mt-5 text-base text-slate-400 leading-relaxed max-w-md">
            Resolve any PAN, NGO Darpan ID, FCRA number or CSR-1 to its linked identities —
            with a measured confidence label on every link. No raw scores, no guesswork.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-lg">
            {[
              { icon: Network, t: "216,545", s: "mapped NGOs" },
              { icon: ShieldCheck, t: "100% / ≥98.7%", s: "tier accuracy" },
              { icon: Database, t: "3 registries", s: "IT · Darpan · FCRA" },
            ].map((f, i) => (
              <div key={i} className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
                <f.icon className="w-4 h-4 text-blue-400 mb-2" />
                <div className="font-mono text-sm font-semibold">{f.t}</div>
                <div className="text-[11px] text-slate-500">{f.s}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 font-mono text-[11px] text-slate-600">
          Income Tax · NGO Darpan (NITI Aayog) · FCRA (MHA) · MCA CSR-1
        </div>
      </div>

      {/* Right: auth card */}
      <div className="lg:w-[460px] flex items-center justify-center p-8 border-t lg:border-t-0 lg:border-l border-slate-800/80 bg-slate-900/40 backdrop-blur-xl">
        <div className="w-full max-w-sm animate-fade-up">
          <GitCompareArrows className="w-7 h-7 text-emerald-600 mb-5" />
          <h2 className="text-2xl font-bold tracking-tight">Sign in to RADAR</h2>
          <p className="mt-2 text-sm text-slate-400">
            Access is restricted to authorised accounts. Sign in with Google or request a one-time email code.
          </p>

          {error && (
            <div data-testid="login-error" className="mt-5 rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-2.5 text-sm text-rose-300">
              {error}
            </div>
          )}
          {notice && !error && (
            <div data-testid="login-notice" className="mt-5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">
              {notice}
            </div>
          )}

          {mode === "choose" && (
            <div className="mt-6 space-y-3">
              <button
                onClick={handleGoogle}
                data-testid="auth-google-login-btn"
                className="group flex w-full items-center justify-center gap-3 rounded-full border border-[#e3dccb] bg-white px-6 py-3.5 text-sm font-semibold text-[#1b1206] transition-colors duration-150 hover:bg-[#f3ede1]"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
                Continue with Google
                <ArrowRight className="w-4 h-4 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all duration-150" />
              </button>

              <div className="flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-slate-800" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">or</span>
                <div className="h-px flex-1 bg-slate-800" />
              </div>

              <button
                onClick={() => { setMode("email"); setError(""); setNotice(""); }}
                data-testid="auth-email-code-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-6 py-3.5 text-sm font-semibold text-slate-200 hover:bg-slate-800 transition-colors duration-150"
              >
                <Mail className="w-4 h-4" /> Sign in with email code
              </button>
            </div>
          )}

          {mode === "email" && step === "email" && (
            <form onSubmit={requestCode} className="mt-6 space-y-3">
              <input
                type="email" required autoFocus value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@organisation.org"
                data-testid="email-input" className={inputCls}
              />
              <button
                type="submit" disabled={busy} data-testid="send-code-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#8a1420] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#6f101a] disabled:opacity-50 transition-colors duration-150"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send me a code
              </button>
              <button type="button" onClick={resetEmail} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
            </form>
          )}

          {mode === "email" && step === "code" && (
            <form onSubmit={verify} className="mt-6 space-y-3">
              <p className="text-xs text-slate-500">Enter the 6-digit code sent to <span className="text-slate-300">{email}</span></p>
              <input
                inputMode="numeric" pattern="[0-9]*" maxLength={6} required autoFocus value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                data-testid="code-input"
                className={`${inputCls} text-center text-2xl font-bold tracking-[0.5em]`}
              />
              <button
                type="submit" disabled={busy || code.length < 6} data-testid="verify-code-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#8a1420] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#6f101a] disabled:opacity-50 transition-colors duration-150"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Verify & sign in
              </button>
              <div className="flex items-center justify-between">
                <button type="button" onClick={resetEmail} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300">
                  <ArrowLeft className="w-3.5 h-3.5" /> Use another email
                </button>
                <button
                  type="button" disabled={resendIn > 0 || busy} onClick={requestCode}
                  data-testid="resend-code-btn"
                  className="text-xs text-blue-400 hover:text-blue-300 disabled:text-slate-600"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}

          <p className="mt-6 font-mono text-[11px] leading-relaxed text-slate-600">
            By continuing you agree to RADAR's acceptable-use policy. Access is limited to users authorised by an administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
